/**
 * Production-Ready Analytics Plugin
 *
 * Handles: GTM, GA4, Consent Mode, Tag Assistant
 * Clean, optimized, single source of truth
 */

export default defineNuxtPlugin(() => {
  if (process.server) return

  // Get configuration
  const config = useRuntimeConfig()
  const gtmId = config.public.googleTagManagerId
  const gaId = config.public.googleAnalyticsId

  if (!gtmId && !gaId) return

  // Initialize tracking foundation
  window.dataLayer = window.dataLayer || []
  window.gtag = window.gtag || function() { window.dataLayer?.push(arguments) }

  // Check if debug mode
  const isDebugMode = window.location.search.includes('gtm_debug') ||
                      window.location.search.includes('_dbg=')

  // Set initial consent (permissive for debug, restrictive for production)
  window.gtag('consent', 'default', {
    ad_storage: isDebugMode ? 'granted' : 'denied',
    analytics_storage: isDebugMode ? 'granted' : 'denied',
    functionality_storage: isDebugMode ? 'granted' : 'denied',
    personalization_storage: isDebugMode ? 'granted' : 'denied',
    ad_user_data: isDebugMode ? 'granted' : 'denied',
    ad_personalization: isDebugMode ? 'granted' : 'denied',
    wait_for_update: isDebugMode ? 500 : 2000
  })

  // Load GA4 if provided
  if (gaId) {
    const gaScript = document.createElement('script')
    gaScript.async = true
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`
    document.head.appendChild(gaScript)

    window.gtag('config', gaId, {
      anonymize_ip: true,
      send_page_view: true
    })
  }

  // Load GTM if provided
  if (gtmId) {
    // Initialize GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      })
    }

    const gtmScript = document.createElement('script')
    gtmScript.async = true
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`
    document.head.appendChild(gtmScript)

    // Noscript fallback
    const noscript = document.createElement('noscript')
    const iframe = document.createElement('iframe')
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`
    iframe.height = '0'
    iframe.width = '0'
    iframe.style.display = 'none'
    iframe.style.visibility = 'hidden'
    noscript.appendChild(iframe)
    document.body.appendChild(noscript)
  }

  // Consent management
  const updateConsent = (granted: boolean) => {
    // Update Google Consent Mode
    window.gtag('consent', 'update', {
      ad_storage: granted ? 'granted' : 'denied',
      analytics_storage: granted ? 'granted' : 'denied',
      functionality_storage: granted ? 'granted' : 'denied',
      personalization_storage: granted ? 'granted' : 'denied',
      ad_user_data: granted ? 'granted' : 'denied',
      ad_personalization: granted ? 'granted' : 'denied'
    })

    // Store consent
    localStorage.setItem('cookie-consent', granted ? 'accepted' : 'rejected')
  }

  // Handle existing consent
  const checkStoredConsent = () => {
    try {
      // Check detailed format
      const detailedConsent = localStorage.getItem('fodboldpakker_cookie_consent')
      if (detailedConsent) {
        const parsed = JSON.parse(detailedConsent)
        if (parsed.preferences?.analytics) {
          updateConsent(true)
          return
        }
      }

      // Check simple format
      const simpleConsent = localStorage.getItem('cookie-consent')
      if (simpleConsent === 'accepted') {
        updateConsent(true)
      }
    } catch (error) {
      console.warn('Error checking stored consent:', error)
    }
  }

  // Initialize
  onMounted(() => {
    // Auto-grant consent for debug mode
    if (isDebugMode) {
      updateConsent(true)
      localStorage.setItem('fodboldpakker_cookie_consent', JSON.stringify({
        version: '1.0',
        timestamp: new Date().toISOString(),
        preferences: { analytics: true, marketing: true, functional: true, essential: true }
      }))
    } else {
      // Check existing consent
      setTimeout(checkStoredConsent, 500)
    }

    // Listen for consent events
    document.addEventListener('cookie-consent-granted', () => updateConsent(true))
    document.addEventListener('cookie-consent-denied', () => updateConsent(false))

    document.addEventListener('cookieyes_consent_update', (event: any) => {
      const accepted = event.detail?.accepted || event.detail?.preferences?.analytics
      updateConsent(!!accepted)
    })
  })

  // Development helpers
  if (process.env.NODE_ENV === 'development' || isDebugMode) {
    (window as any).analytics = {
      grantConsent: () => updateConsent(true),
      denyConsent: () => updateConsent(false),
      clearConsent: () => {
        localStorage.removeItem('cookie-consent')
        localStorage.removeItem('fodboldpakker_cookie_consent')
        location.reload()
      },
      status: () => ({
        gtmId,
        gaId,
        gtmLoaded: !!window.google_tag_manager?.[gtmId],
        consent: localStorage.getItem('cookie-consent')
      })
    }
  }

  // Return utilities
  return {
    provide: {
      analytics: {
        track: (event: string, data?: object) => {
          window.dataLayer?.push({ event, ...data })
        }
      }
    }
  }
})
